import { STATUS_ARROW_WIDGET_TYPE } from '../../../widgets/assetStatusUtils';

export const clampDropPointToBounds = ({
	left,
	top,
	minLeft,
	maxLeft,
	minTop,
	maxTop,
}) => ({
	left: Math.max(minLeft, Math.min(left, maxLeft)),
	top: Math.max(minTop, Math.min(top, maxTop)),
});

export const getDropContainerStyleMetrics = ({
	divertedElement,
	divertedRect,
	metrics,
}) => {
	const divertedStyle = divertedElement ? document.defaultView.getComputedStyle(divertedElement) : null;
	const gap = divertedStyle ? (parseInt(divertedStyle.gap, 10) || 0) : 0;
	const padLeft = divertedStyle ? (parseInt(divertedStyle.paddingLeft, 10) || 0) : 0;
	const padRight = divertedStyle ? (parseInt(divertedStyle.paddingRight, 10) || 0) : 0;
	const padTop = divertedStyle ? (parseInt(divertedStyle.paddingTop, 10) || 0) : 0;
	const padBottom = divertedStyle ? (parseInt(divertedStyle.paddingBottom, 10) || 0) : 0;
	const dropScrollLeft = Math.max(0, divertedElement.scrollLeft || 0);
	const dropScrollTop = Math.max(0, divertedElement.scrollTop || 0);
	const effectiveDropWidth = Math.max(
		metrics.width || 0,
		dropScrollLeft + Math.max(1, divertedElement.clientWidth || Math.round(divertedRect.width || 0)),
	);
	const effectiveDropHeight = Math.max(
		metrics.height || 0,
		dropScrollTop + Math.max(1, divertedElement.clientHeight || Math.round(divertedRect.height || 0)),
	);

	return {
		gap,
		padLeft,
		padRight,
		padTop,
		padBottom,
		dropScrollLeft,
		dropScrollTop,
		effectiveDropWidth,
		effectiveDropHeight,
	};
};

export const getDropWidgetSize = ({
	widgetWidth,
	widgetHeight,
	minimumWidgetSize,
	effectiveDropWidth,
	effectiveDropHeight,
	padLeft,
	padRight,
	padTop,
	padBottom,
}) => {
	const minimumWidth = minimumWidgetSize.width;
	const minimumHeight = minimumWidgetSize.height;
	const normalizedWidgetWidth = Math.max(minimumWidth, widgetWidth);
	const normalizedWidgetHeight = Math.max(minimumHeight, widgetHeight);

	return {
		widgetWidth: Math.min(normalizedWidgetWidth, Math.max(minimumWidth, effectiveDropWidth - padLeft - padRight)),
		widgetHeight: Math.min(normalizedWidgetHeight, Math.max(minimumHeight, effectiveDropHeight - padTop - padBottom)),
	};
};

export const getDropBounds = ({
	effectiveDropWidth,
	effectiveDropHeight,
	widgetWidth,
	widgetHeight,
	padLeft,
	padRight,
	padTop,
	padBottom,
	defaultDivWidth,
	defaultDivHeight,
	gap,
}) => {
	const minLeft = padLeft;
	const minTop = padTop;

	return {
		gridColumnSize: defaultDivWidth + gap,
		gridRowSize: defaultDivHeight + gap,
		minLeft,
		maxLeft: Math.max(minLeft, effectiveDropWidth - padRight - widgetWidth),
		minTop,
		maxTop: Math.max(minTop, effectiveDropHeight - padBottom - widgetHeight),
		usableWidth: Math.max(0, effectiveDropWidth - padLeft - padRight),
		usableHeight: Math.max(0, effectiveDropHeight - padTop - padBottom),
	};
};

export const getRelativeDropPoint = ({
	intendedLeftGlobal,
	intendedTopGlobal,
	divertedRect,
	dropScrollLeft,
	dropScrollTop,
}) => ({
	left: intendedLeftGlobal - divertedRect.left + dropScrollLeft,
	top: intendedTopGlobal - divertedRect.top + dropScrollTop,
});

export const snapDropPointToGrid = ({
	left,
	top,
	minLeft,
	minTop,
	gridColumnSize,
	gridRowSize,
}) => ({
	left: minLeft + (Math.round((left - minLeft) / gridColumnSize) * gridColumnSize),
	top: minTop + (Math.round((top - minTop) / gridRowSize) * gridRowSize),
});

export const getInitialDropPoint = ({
	relativeDropPoint,
	isFreeformLayoutWidget,
	minLeft,
	maxLeft,
	minTop,
	maxTop,
	gridColumnSize,
	gridRowSize,
}) => {
	const snappedDropPoint = snapDropPointToGrid({
		left: relativeDropPoint.left,
		top: relativeDropPoint.top,
		minLeft,
		minTop,
		gridColumnSize,
		gridRowSize,
	});

	return clampDropPointToBounds({
		left: isFreeformLayoutWidget ? relativeDropPoint.left : snappedDropPoint.left,
		top: isFreeformLayoutWidget ? relativeDropPoint.top : snappedDropPoint.top,
		minLeft,
		maxLeft,
		minTop,
		maxTop,
	});
};

export const getDropSearchStep = ({
	isFreeformLayoutWidget,
	gridColumnSize,
	gridRowSize,
	freeformSearchStep,
}) => ({
	horizontalSearchStep: isFreeformLayoutWidget ? freeformSearchStep : gridColumnSize,
	verticalSearchStep: isFreeformLayoutWidget ? freeformSearchStep : gridRowSize,
});

export const getDropSearchGridSize = ({
	usableWidth,
	usableHeight,
	widgetWidth,
	widgetHeight,
	horizontalSearchStep,
	verticalSearchStep,
}) => ({
	maxCols: Math.max(0, Math.floor((usableWidth - widgetWidth) / Math.max(1, horizontalSearchStep)) + 1),
	maxRows: Math.max(0, Math.floor((usableHeight - widgetHeight) / Math.max(1, verticalSearchStep)) + 1),
});

export const isDropCandidateWithinBounds = ({
	left,
	top,
	minLeft,
	minTop,
	widgetWidth,
	widgetHeight,
	effectiveDropWidth,
	effectiveDropHeight,
	padRight,
	padBottom,
}) => (
	left >= minLeft &&
	top >= minTop &&
	left + widgetWidth <= effectiveDropWidth - padRight &&
	top + widgetHeight <= effectiveDropHeight - padBottom
);

export const getNearestAvailableDropCandidate = ({
	maxCols,
	maxRows,
	minLeft,
	minTop,
	horizontalSearchStep,
	verticalSearchStep,
	widgetWidth,
	widgetHeight,
	effectiveDropWidth,
	effectiveDropHeight,
	padRight,
	padBottom,
	initialDroppedLeftRelative,
	initialDroppedTopRelative,
	checkOverlap,
	currentWidgets,
	wid,
	draggedWidget,
}) => {
	let bestCandidateX = null;
	let bestCandidateY = null;
	let minDistance = Infinity;
	let foundValidSpot = false;

	for (let row = 0; row < maxRows; row++) {
		for (let col = 0; col < maxCols; col++) {
			const testLeft = minLeft + (col * horizontalSearchStep);
			const testTop = minTop + (row * verticalSearchStep);

			if (!isDropCandidateWithinBounds({
				left: testLeft,
				top: testTop,
				minLeft,
				minTop,
				widgetWidth,
				widgetHeight,
				effectiveDropWidth,
				effectiveDropHeight,
				padRight,
				padBottom,
			})) {
				continue;
			}
			if (checkOverlap(testLeft, testTop, widgetWidth, widgetHeight, currentWidgets, wid, draggedWidget)) {
				continue;
			}

			const dx = initialDroppedLeftRelative - testLeft;
			const dy = initialDroppedTopRelative - testTop;
			const distance = (dx * dx) + (dy * dy);
			if (distance < minDistance) {
				minDistance = distance;
				bestCandidateX = testLeft;
				bestCandidateY = testTop;
				foundValidSpot = true;
			}
		}
	}

	return {
		bestCandidateX,
		bestCandidateY,
		foundValidSpot,
	};
};

export const shouldSnapWaterNetworkDropPoint = ({
	isFreeformLayoutWidget,
	isWaterNetworkWidget,
	isWaterNetworkLinkEnabled,
}) => (
	isFreeformLayoutWidget &&
	isWaterNetworkWidget &&
	isWaterNetworkLinkEnabled
);

export const getSnappedWaterNetworkDropPoint = ({
	left,
	top,
	width,
	height,
	draggedWidget,
	currentWidgets,
	getWidgetRectPx,
	metrics,
	ignoredWidgetId,
	snapWaterNetworkMoveRect,
	minLeft,
	maxLeft,
	minTop,
	maxTop,
}) => {
	const snappedWaterRect = snapWaterNetworkMoveRect(
		{
			left,
			top,
			width,
			height,
		},
		draggedWidget,
		currentWidgets,
		getWidgetRectPx,
		metrics,
		ignoredWidgetId,
	);

	return clampDropPointToBounds({
		left: snappedWaterRect.left,
		top: snappedWaterRect.top,
		minLeft,
		maxLeft,
		minTop,
		maxTop,
	});
};

export const isImmediateDropAllowedWidget = (type) => (
	type === 'background-image' || type === 'box-widget' || type === STATUS_ARROW_WIDGET_TYPE
);

export const shouldShowNoDropSpaceAlert = ({
	foundValidSpot,
	type,
}) => (
	!foundValidSpot && !isImmediateDropAllowedWidget(type)
);
